import * as mqtt from "mqtt"
import { MqttClient } from "mqtt"
import type { BambuClientEvents } from "src/BambuClientEvents"
import * as events from "eventemitter3"
import { AbstractCommand, GetVersionCommand, PushAllCommand } from "src/commands"
import {
	isGetVersionCommand,
	isInfoMessage,
	isMCPrintMessage,
	isPrintMessage,
	isPushAllCommand,
	isPushStatusCommand,
	PushAllResponse as PushAllCommandResponse,
} from "src/responses"
import {
	BambuClientPrinterStatus,
	CommandResponse,
	IncomingMessageData,
	PrinterData,
	PrinterModelNamesShort,
} from "src/types"
import { Job } from "./Job"
import { createId } from "@paralleldrive/cuid2"
import sleep from "src/utils/sleep"
import { BambuClientOptions } from "src/BambuClientOptions"
import subtractObjects from "./utils/subtractObject"
// import { isValidSequenceId } from "./utils/sequenceIdManager"

/**
 * Manages connectivity and messages from/to the printer.
 */
export class BambuClient extends events.EventEmitter<keyof BambuClientEvents> {
	public override emit<K extends keyof BambuClientEvents>(
		event: K,
		...arguments_: BambuClientEvents[K]
	): boolean
	public override emit(event: string, ...arguments_: any[]): boolean {
		return super.emit(event as keyof BambuClientEvents, ...arguments_)
	}

	public override off<K extends keyof BambuClientEvents>(
		event: K,
		listener?: (...arguments_: BambuClientEvents[K]) => void
	): this
	public override off(event: string, listener?: (...arguments_: any[]) => void): this {
		super.off(event as keyof BambuClientEvents, listener)

		return this
	}

	public override once<K extends keyof BambuClientEvents>(
		event: K,
		listener: (...arguments_: BambuClientEvents[K]) => void
	): this
	public override once(event: string, listener: (...arguments_: any[]) => void): this {
		super.once(event as keyof BambuClientEvents, listener)

		return this
	}

	public override on<K extends keyof BambuClientEvents>(
		event: K,
		listener: (...arguments_: BambuClientEvents[K]) => void
	): this
	public override on(event: string, listener: (...arguments_: any[]) => void): this {
		super.on(event as keyof BambuClientEvents, listener)

		return this
	}

	public id: string = createId()

	private mqttClient: mqtt.MqttClient | undefined = undefined

	public config

	private _printerData: PrinterData = {
		modules: [],
		model: undefined,
		sequence_id: "20000", // Initialize with START_SEQ_ID as string
	}
	private _printerStatus: BambuClientPrinterStatus = "OFFLINE"

	public jobHistory: Job[] = []
	public currentJob: Job | null = null

	private isJobInHistory(job: Job): boolean {
		return this.jobHistory.some(
			historyJob =>
				historyJob.data.id === job.data.id ||
				(historyJob.data.gcodeName === job.data.gcodeName &&
					historyJob.data.printerId === job.data.printerId)
		)
	}

	private addJobToHistory(job: Job): void {
		if (!this.isJobInHistory(job)) {
			console.log("addJobToHistory", job)
			this.jobHistory.push(job)
		}
	}

	public constructor(public readonly clientOptions: BambuClientOptions) {
		super()

		this.config = {
			host: clientOptions.host,
			port: clientOptions.port ?? 8883,
			accessToken: clientOptions.accessToken,
			serialNumber: clientOptions.serialNumber,
			throwOnOfflineCommands: clientOptions.throwOnOfflineCommands ?? false,
			reconnectInterval: clientOptions.reconnectInterval ?? 1000,
			connectTimeout: clientOptions.connectTimeout ?? 2000,
			keepAlive: clientOptions.keepAlive ?? 20,
		}

		this.jobHistory = clientOptions.jobHistory ?? []
		this.currentJob = clientOptions.currentJob ?? null
	}

	private async connectMQTT() {
		return new Promise<mqtt.IConnackPacket>((resolve, reject) => {
			// make sure that we are not already connected
			if (this.mqttClient)
				throw new Error("Can't establish a new connection while running another one!")

			this.mqttClient = mqtt.connect(`mqtts://${this.config.host}:${this.config.port}`, {
				username: "bblp",
				password: this.config.accessToken,
				reconnectPeriod: this.clientOptions.reconnectInterval,
				connectTimeout: this.clientOptions.connectTimeout,
				keepalive: this.clientOptions.keepAlive,
				resubscribe: true,
				rejectUnauthorized: false,
				protocol: "mqtts",
			})

			this.mqttClient.on("connect", async (...args) => {
				// while we did connect, we only resolve the promise once the onConnect logic has also (successfully) completed
				const response = await this.onConnect(...args)

				this.emit("client:connect")

				resolve(response)
			})

			this.mqttClient.on("disconnect", () => {
				this.emit("client:disconnect", false)

				this.emit("printer:statusUpdate", this._printerStatus, "OFFLINE")
				this._printerStatus = "OFFLINE"

				if (this.currentJob) this.emit("job:pause", this.currentJob, true)
			})

			this.mqttClient.on("offline", () => {
				this.emit("client:disconnect", true)

				this.emit("printer:statusUpdate", this._printerStatus, "OFFLINE")
				this._printerStatus = "OFFLINE"

				if (this.currentJob) this.emit("job:pause", this.currentJob, true)
			})

			this.mqttClient.on("message", (topic, message) =>
				this.emit("rawMessage", topic, message)
			)

			this.mqttClient.on("error", err => {
				this.emit("client:error", err)

				reject(err) // this will obviously only work if we haven't connected before
			})
		})
	}

	/**
	 * Connect to the printer.
	 */
	public async connect() {
		return await Promise.all([this.connectMQTT()])
	}

	/**
	 * Disconnect from the printer.
	 * @param force Forcefully disconnect.
	 * @param options {Parameters<MqttClient["end"]>[1]} MQTT client options
	 */
	public async disconnect(force = false, options?: Parameters<MqttClient["end"]>[1]) {
		return new Promise(resolve => this.mqttClient?.end(force, options, resolve))
	}

	private subscribe(topic: string): Promise<void> {
		if (!(this._printerStatus === "OFFLINE") && this.config.throwOnOfflineCommands)
			throw new Error(
				`Unable to subscribe to topic "${topic}" while disconnected from printer!`
			)

		return new Promise<void>((resolve, reject) => {
			if (!this.mqttClient) {
				return reject("Client not connected.")
			}

			this.mqttClient.subscribe(topic, (error: any) => {
				if (error) {
					return reject(`Error subscribing to topic '${topic}': ${error.message}`)
				}
			})

			const listener = async (receivedTopic: string, payload: Buffer) => {
				if (receivedTopic !== topic) return

				await this.onMessage(payload.toString(), topic)
			}

			this.mqttClient.on("message", listener)

			resolve()
		})
	}

	/**
	 * Execute a command.
	 * @param command {AbstractCommand} Command to execute.
	 * @returns {Promise<CommandResponse>} A promise which resolves to the CommandResponse once the command has been acknowledged by the printer, times out after 5 seconds.
	 */
	public async executeCommand(
		command: AbstractCommand,
		timeoutMs: number = 5000
	): Promise<CommandResponse> {
		if (!(this._printerStatus === "OFFLINE") && this.config.throwOnOfflineCommands)
			throw new Error(`Unable to send commands while disconnected from printer!`)
		return new Promise(async (resolve, reject) => {
			// run command
			const commandSeqId = await command.invoke(this).catch(err => reject(err))

			// start a timeout which rejects the promise after 5 seconds
			const timeout = setTimeout(() => {
				reject(
					new Error(
						`Command execution timed out after 5 seconds. ${this.config.serialNumber}`
					)
				)
			}, timeoutMs)

			// wait for a response
			this.on("rawMessage", (topic: string, payload: Buffer) => {
				const data = JSON.parse(payload.toString())
				const key = Object.keys(data)[0]

				if (!(isInfoMessage(data) || isMCPrintMessage(data) || isPrintMessage(data)))
					return

				const response = (data as unknown as IncomingMessageData)[key] as CommandResponse
				if (!response?.command) return

				// Validate sequence_id in response
				if (response.sequence_id) {
					const numericId = parseInt(response.sequence_id, 10)
					// if (isNaN(numericId) || !isValidSequenceId(numericId)) {
					if (isNaN(numericId)) {
						reject(new Error(`Invalid sequence_id in response: ${response.sequence_id}`))
						return
					}
				}

				// It's possible that we receive a message between sending the command
				if (command.ownsResponse(response, commandSeqId ?? undefined)) {
					// Verify sequence_id matches the command
					// const commandId = command.extra?.sequence_id;
					// if (commandId && commandId !== response.sequence_id) {
					// 	reject(new Error(`Response sequence_id mismatch. Expected: ${commandId}, Got: ${response.sequence_id}`));
					// 	return;
					// }
					// clear the timeout and resolve the promise
					clearTimeout(timeout)
					resolve(response)
				}

				// X1C send a push_status, if we are getting this message
				if (command.command === "get_version" && response.sequence_id === "2021") {
					clearTimeout(timeout)
					resolve(response)
				}
			})
		})
	}

	protected async onConnect(packet: mqtt.IConnackPacket) {
		// subscribe to the only available topic (report)
		await this.subscribe(`device/${this.config.serialNumber}/report`)

		// sleep for a second so the printer has time to process the subscription
		await sleep(2000)

		// request printer version data
		await this.executeCommand(new GetVersionCommand())

		// request full printer data on P1 && A1 series printers
		// because they don't send it each time like the X1 series
		if (
			this._printerData.model === PrinterModelNamesShort.P1P ||
			this._printerData.model === PrinterModelNamesShort.P1S ||
			this._printerData.model === PrinterModelNamesShort.A1 ||
			this._printerData.model === PrinterModelNamesShort.A1M
		) {
			// request full printer data
			await this.executeCommand(new PushAllCommand(), 5000)

			// request full printer data every 5 minutes
			// this ensures compatibility with the p1 series' "slow" ESP32 controller
			setInterval(
				async () => {
					await this.executeCommand(new PushAllCommand())
				},
				5 * 60 * 1000 // 5 minutes
			)
		}

		return packet
	}

	private async onMessage(packet: string, topic: string) {
		const data = JSON.parse(packet)
		const key = Object.keys(data)[0]
		if (!(isInfoMessage(data) || isMCPrintMessage(data) || isPrintMessage(data))) return

		if (isPrintMessage(data)) {
			this.emit("message", topic, key, data.print)

			if (isPushAllCommand(data.print) || isPushStatusCommand(data.print)) {
				// Check for AMS updates before merging data
				if (data.print.ams) {
					const previousAms = this._printerData.ams;
					if (!previousAms || Object.keys(subtractObjects(previousAms, data.print.ams)).length > 0) {
						this.emit("printer:amsUpdate", data.print.ams);
					}
				}

				// merge the new data with the old data
				this._printerData = {
					...this._printerData,
					...data.print,
				}

				this.emit("printer:dataUpdate", this._printerData, data.print)

				// printer status updates and job creation
				if (data.print.gcode_state && data.print.gcode_state !== this._printerStatus) {
					let oldStatus = this._printerStatus
					let newStatus = data.print.gcode_state

					this.emit("printer:statusUpdate", oldStatus, newStatus)

					// we treat PREPARE and SLICING as an alias of idle because they're of no use
					if (newStatus === "PREPARE" || newStatus === "SLICING") newStatus = "IDLE"
					if (oldStatus === "PREPARE" || oldStatus === "SLICING") oldStatus = "RUNNING"

					if (
						(oldStatus === "IDLE" || oldStatus === "FINISH" || oldStatus === "FAILED" || oldStatus === "RUNNING") &&
						newStatus === "RUNNING"
					) {
						// print start

						// move current job to jobHistory
						if (this.currentJob !== null) this.addJobToHistory(this.currentJob)

						// create new job and set it as the current job
						this.currentJob = new Job(
							this._printerData as PushAllCommandResponse, // we get all the data right at initialization => we must have all of it by now
							this.id,
							this._printerData.model! // we get the printer model right at initialization => we must have it by now
						)

						this.emit("job:start", this.currentJob)
					} else if (
						oldStatus === "RUNNING" &&
						(newStatus === "FINISH" || newStatus === "FAILED" || newStatus === "IDLE")
					) {
						// print finish

						if (newStatus === "FINISH") {
							// finished with no errors

							if (this.currentJob !== null)
								this.emit("job:finish", this.currentJob, "SUCCESS")
						} else if (newStatus === "FAILED") {
							// finished with errors

							if (this.currentJob !== null)
								this.emit("job:finish", this.currentJob, "FAILED")
						} else if (newStatus === "IDLE") {
							// unexpected finish

							if (this.currentJob !== null)
								this.emit("job:finish", this.currentJob, "UNEXPECTED")
						}

						// move currentJob to jobHistory
						if (this.currentJob !== null) this.addJobToHistory(this.currentJob)

						// empty currentJob
						this.currentJob = null
					} else if (oldStatus === "RUNNING" && newStatus === "PAUSE") {
						// paused
						if (this.currentJob) this.emit("job:pause", this.currentJob, false)
					} else if (oldStatus === "PAUSE" && newStatus === "RUNNING") {
						// unpaused

						if (this.currentJob) this.emit("job:unpause", this.currentJob)
					} else if (oldStatus === "OFFLINE" && newStatus === "PAUSE") {
						// this happens when the printer resumes a job from a power outage
						this.emit("job:offlineRecovery", this.currentJob!) // if the new status is paused, then the client should already have a currentJob
					} else if (oldStatus === "OFFLINE" && newStatus === "RUNNING") {
						// this happens when the client connects to the printer while it already has an ongoing job

						// move current job to jobHistory
						if (this.currentJob !== null) {
							this.addJobToHistory(this.currentJob)
						}

						if (this._printerData) {
							// create new job and set it as the current job
							this.currentJob = new Job(
								this._printerData as PushAllCommandResponse, // we get all the data right at initialization => we must have all of it by now
								this.id,
								(this._printerData as PrinterData).model! // we get the printer model right at initialization => we must have it by now
							)
						}
					} else if (
						(oldStatus === "IDLE" || oldStatus === "OFFLINE") &&
						(newStatus === "FINISH" || newStatus === "FAILED" || newStatus === "IDLE")
					) {
						// this usually only happens when the client connects, all of which we ignore
					} else if (
						(oldStatus === "FINISH" || newStatus === "FAILED") &&
						newStatus === "IDLE"
					) {
						// this happens right before a job start, but since IDLE doesn't mean the print has started, we don't do anything
					} else if (oldStatus === "FAILED" && newStatus === "IDLE") {
						// Handle case that previous job was cancelled and a new one has been started.
					} else if (oldStatus === "PAUSE" && newStatus === "FAILED") {
						// If the printer is paused and then a job is cancelled.
						if (this.currentJob !== null) {
							this.emit("job:finish", this.currentJob, "FAILED")
						}
					} else if (oldStatus !== newStatus) {
						// does not match but not "caught" before
						throw new Error(
							`Edge case detected while updating printer status! - old: ${oldStatus} | new: ${newStatus}`
						)
					}

					// set old status as the new status
					this._printerStatus = data.print.gcode_state
					// update job data
					if (this.currentJob) {
						const previousJobData = this.currentJob?.data
						console.log('Previous job data:', previousJobData)

						this.currentJob.update(data.print)
						console.log('Updated job data:', this.currentJob.data)

						const updatePackage = subtractObjects(previousJobData, this.currentJob.data)
						console.log('Update package:', updatePackage)

						this.emit(
							"job:update",
							this.currentJob,
							updatePackage
						)
						console.log('Emitted job:update event')
					} else {
						console.log('No current job found when trying to emit job:update')
					}
				}

				if (data.print.gcode_state === "RUNNING") {
					// update job data
					if (this.currentJob) {
						const previousJobData = this.currentJob?.data

						this.currentJob.update(data.print)

						this.emit(
							"job:update",
							this.currentJob,
							subtractObjects(previousJobData, this.currentJob.data)
						)
					}
				} 
			}
		}

		if (isMCPrintMessage(data)) {
			this.emit("message", topic, key, data.print)
		}

		if (isInfoMessage(data)) {

			this.emit("message", topic, key, data.info)

			if (isGetVersionCommand(data.info)) {
				// merge the new data with the old data (without duplicates)
				const newData = [...new Set([...this._printerData.modules, ...data.info.module])]
				this._printerData.modules = newData

				this.emit("printer:dataUpdate", this._printerData, { modules: newData })

				// figure out the printer version and add it to the printer data
				// we do this by checking the serial number of OTA module
				const otaModule = this._printerData.modules.find(module => module.name === "ota")
				if (!otaModule)
					throw new Error(
						"OTA module version data not found, unable to determine printer model!"
					)

				if (otaModule.sn.startsWith("00M") || this.config.serialNumber.startsWith("00M"))
					this._printerData.model = PrinterModelNamesShort.X1C
				else if (otaModule.sn.startsWith("00W"))
					this._printerData.model = PrinterModelNamesShort.X1
				else if (otaModule.sn.startsWith("03W"))
					this._printerData.model = PrinterModelNamesShort.X1E
				else if (otaModule.sn.startsWith("01S"))
					this._printerData.model = PrinterModelNamesShort.P1P
				else if (otaModule.sn.startsWith("01P"))
					this._printerData.model = PrinterModelNamesShort.P1S
				else if (otaModule.sn.startsWith("030"))
					this._printerData.model = PrinterModelNamesShort.A1
				else if (otaModule.sn.startsWith("039"))
					this._printerData.model = PrinterModelNamesShort.A1M
				else {
					console.log({ otaModule, data: this.config.serialNumber })
					throw new Error(`Printer model not supported!: ${otaModule.sn}`)
				}

				this.emit("printer:dataUpdate", this._printerData, {
					model: this._printerData.model,
				})
			}
		}
	}

	/**
	 * Used by the individual command types to publish messages to the printer.
	 * @param message
	 */
	public publish(message: string | object): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			if (!this.mqttClient) {
				return reject("Client not connected.")
			}

			const message_ = typeof message === "string" ? message : JSON.stringify(message)

			const topic = `device/${this.config.serialNumber}/request`

			this.mqttClient.publish(topic, message_, error => {
				if (error) {
					return reject(`Error publishing to topic '${topic}': ${error.message}`)
				}

				resolve()
			})
		})
	}

	public get data(): PrinterData {
		return this._printerData
	}

	public get connectionStatus() {
		return this.mqttClient?.connected
	}

	public get status(): BambuClientPrinterStatus {
		return this._printerStatus
	}
}
